import { datadogLogs } from '@datadog/browser-logs';
import { CamelRequestError, type MyProfile } from '@onestep/camel-api';
import { useViewerProfile } from '@onestep/camel-api/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { isCancel } from 'axios';
import { useEffect, useRef } from 'react';
import { Cookies } from 'react-cookie';
import { useLocation, useNavigate } from 'react-router';
import { useIntercom } from 'react-use-intercom';
import { ZodError } from 'zod';

import { customSelectorIntercomId } from '../../App';
import { useFeatureFlags } from '../../components/FeatureFlagProvider';
import { clearUser } from '../../config/datadog';
import { logout as mixPanelLogout } from '../../config/mixpanel';
import { type IClinicUser } from '../../Pages/store';
import { SENDBIRD_TOKEN_ID } from '../../services/communicationService/sendbirdAuthenticationService/types';
import { getPortalRuntimeConfig } from '../../services/runtimeConfig';
import useDebug from '../useDebug';
import { useResetStore } from '../useStores';
import { type IUseLoggedUser, type LogoutOptions, type UseLoggedUserOptions } from './types';

const cookies = new Cookies();

const nonUsersSignupUrls = ['signup'];

const nonUsersUrls = [...nonUsersSignupUrls, 'login'];

const SESSION_REJECTED_STATUSES = [401, 403];

const MAX_PROFILE_FETCH_RETRIES = 2;

const isSessionRejectedError = (error: unknown): boolean =>
  error instanceof CamelRequestError && SESSION_REJECTED_STATUSES.includes(error.status);

const viewerProfileErrorType = (error: unknown): 'schema_mismatch' | 'http_error' | 'transport' => {
  if (error instanceof ZodError) return 'schema_mismatch';
  if (error instanceof CamelRequestError) return 'http_error';
  return 'transport';
};

export const shouldRetryProfileFetch = (failureCount: number, error: unknown): boolean =>
  !(error instanceof ZodError) &&
  !isCancel(error) &&
  !isSessionRejectedError(error) &&
  failureCount < MAX_PROFILE_FETCH_RETRIES;

export default function useLoggedUser({
  onProfileLoaded,
  redirectsOnSessionEnd = false,
}: UseLoggedUserOptions = {}): IUseLoggedUser {
  const [, , resetDebug] = useDebug('');
  const navigate = useNavigate();
  const location = useLocation();
  const resetStore = useResetStore();
  const queryClient = useQueryClient();
  const { shutdown, boot } = useIntercom();
  const { reset } = useFeatureFlags();
  const token = cookies.get('auth_timeout');
  const isLogoutTriggered = useRef(false);

  const logProfileFailure = (error: unknown) => {
    datadogLogs?.logger?.error('viewer_profile_fetch_failed', {
      error_type: viewerProfileErrorType(error),
      status: error instanceof CamelRequestError ? error.status : undefined,
    });
  };

  const result = useViewerProfile({
    enabled: !!token && !nonUsersSignupUrls.some((url) => location.pathname.includes(url)),
    onSuccess: onProfileLoaded,
    onError: logProfileFailure,
    retry: shouldRetryProfileFetch,
    staleTime: Infinity,
    cacheTime: 0,
  });

  const me = result?.data as MyProfile;
  const isSessionRejected = isSessionRejectedError(result.error);
  const isProfileUnavailable = result.isError && !isSessionRejected;

  const getLoginSearchParams = (): string => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.has('otp') && queryParams.has('uuid') ? queryParams.toString() : '';
  };

  const computeRedirectTo = (): string | undefined => {
    const existing = (location.state as { redirectTo?: string } | null)?.redirectTo;
    if (existing) {
      return existing;
    }
    if (nonUsersUrls.some((x) => location.pathname.includes(x))) {
      return undefined;
    }
    const loginSearchParams = getLoginSearchParams();
    const path = `${location.pathname}${!loginSearchParams ? location.search : ''}${location.hash}`;
    return path === '/' ? undefined : path;
  };

  const navigateToLogin = ({ preserveRedirect }: Pick<LogoutOptions, 'preserveRedirect'>) => {
    const loginSearchParams = preserveRedirect ? getLoginSearchParams() : '';
    const redirectTo = preserveRedirect ? computeRedirectTo() : undefined;
    navigate(`/login${loginSearchParams ? `?${loginSearchParams}` : ''}`, {
      replace: true,
      state: redirectTo ? { redirectTo } : undefined,
    });
  };

  const isProfileLoading = result.isLoading || result.isFetching;
  const hasNoSession =
    !token || isSessionRejected || (!result.isError && !me?.uuid && !isProfileLoading);

  const mutate = useMutation({
    mutationFn: async (body: Partial<IClinicUser>) => {
      await axios.patch('/api/my_profile', body);
      result.refetch();
    },
  });

  const cleanAppCookie = () => {
    cookies.remove('auth_timeout', { path: '/' });
  };

  const logoutAction = useMutation({
    mutationFn: async ({ cancelSource }: LogoutOptions) => {
      if (me && !isLogoutTriggered.current) {
        isLogoutTriggered.current = true;
        const logoutResult = axios.get('/api/logout');
        shutdown();
        result.remove();
        await Promise.allSettled([logoutResult]);
        cancelSource?.cancel();
      }
      queryClient.clear();
      resetStore();
      cleanAppCookie();
      mixPanelLogout();
      resetDebug();
      clearUser();
      reset();
      localStorage.removeItem(SENDBIRD_TOKEN_ID);
      if (getPortalRuntimeConfig().intercom.appId) {
        boot({
          hideDefaultLauncher: true,
          verticalPadding: 50,
          customLauncherSelector: `#${customSelectorIntercomId}`,
        });
      }
    },
    onSuccess: (_data, variables) => {
      isLogoutTriggered.current = false;
      navigateToLogin({ preserveRedirect: variables?.preserveRedirect });
    },
    onError: () => {
      isLogoutTriggered.current = false;
    },
  });

  const isOnSignedOutPage = nonUsersUrls.some((x) => location.pathname.includes(x));
  useEffect(() => {
    if (!redirectsOnSessionEnd || !hasNoSession || isOnSignedOutPage) {
      return;
    }
    if (me?.uuid) {
      logoutAction.mutate({ preserveRedirect: true });
    } else {
      navigateToLogin({ preserveRedirect: true });
    }
  }, [hasNoSession]);

  const organization = me?.org;
  const isOneStep =
    organization?.name === 'onestep' || (me?.email ? me.email.endsWith('@onestep.co') : false);
  const hasNoWorkspaces = !!me?.uuid && me.workspaces.length === 0;

  return {
    myProfile: me,
    update: mutate.mutateAsync,
    isOneStep,
    organization,
    hasNoWorkspaces,
    isProfileUnavailable,
    isLoading: result?.isLoading,
    logout: logoutAction.mutateAsync,
    cleanAppCookie,
    refetchProfile: result.refetch,
  };
}
